import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Send, Hash, X, Search, Film, FileText, CheckSquare, Image as ImageIcon, Paperclip, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import VoiceRecorder from '@/components/ui/VoiceRecorder';
import MentionTextarea from '@/components/creative/MentionTextarea';
import { fetchThreadMessages, postThreadMessage, markThreadRead, searchItems, editComment, deleteComment, type ChatKind, type Creative, type Comment, type CommentRef, type SearchHit, type ChatAttachment } from '@/lib/queries';
import { uploadChatFile } from '@/lib/upload';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/cn';
import { useCollapsed, CollapseChevron } from '@/components/ui/CollapsibleCard';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import PromptDialog from '@/components/ui/PromptDialog';
import { bidi, BIDI_CLS } from '@/lib/bidi';

const REF_RX = /#\[(creative|strategy|task|asset):([^|\]]+)\|([^\]]*)\]/g;
const ICON: Record<string, React.ReactNode> = { creative: <Film size={11} />, strategy: <FileText size={11} />, task: <CheckSquare size={11} />, asset: <ImageIcon size={11} /> };

/** Message text with @people highlighted and #items rendered as links. */
export function ChatBody({ text: raw, refs, className }: { text: string; refs?: CommentRef[]; className?: string }) {
  const text = bidi(raw);
  const byKey = useMemo(() => new Map((refs ?? []).map((r) => [`${r.type}:${r.id}`, r])), [refs]);
  const parts: React.ReactNode[] = [];
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  const rx = new RegExp(REF_RX.source, 'g');
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) parts.push(<Mentions key={i++} text={text.slice(last, m.index)} />);
    const r = byKey.get(`${m[1]}:${m[2]}`);
    const label = m[3] || r?.label || m[2];
    const link = r?.link ?? (m[1] === 'creative' ? `/creatives/${m[2]}` : m[1] === 'strategy' ? `/strategies/${m[2]}` : '#');
    parts.push(
      <Link key={i++} to={link} className="inline-flex items-center gap-1 align-baseline rounded px-1.5 py-0.5 text-[11px] font-medium bg-sky-500/15 text-sky-300 hover:bg-sky-500/25 mx-0.5">
        {ICON[m[1]]} {label}
      </Link>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<Mentions key={i++} text={text.slice(last)} />);
  return <span dir="auto" className={cn('whitespace-pre-wrap break-words', BIDI_CLS, className)}>{parts}</span>;
}

// a web address in a message: http(s)://... or www...; trailing punctuation stays text
const URL_RX = /((?:https?:\/\/|www\.)[^\s<>"']*[^\s<>"'.,;:!?)\]\u200E])/g;

function WebLink({ url }: { url: string }) {
  const href = url.startsWith('www.') ? `https://${url}` : url;
  const here = typeof window !== 'undefined' ? window.location.origin : '';
  const cls = 'text-sky-300 underline underline-offset-2 break-all hover:text-sky-200';
  // a Warehouse link opens in place; anything else in a new tab
  // an address reads left to right even inside an Arabic line
  if (here && href.startsWith(here)) return <Link to={href.slice(here.length) || '/'} dir="ltr" className={`${cls} [unicode-bidi:isolate]`}>{url}</Link>;
  return <a href={href} target="_blank" rel="noopener noreferrer" dir="ltr" className={`${cls} [unicode-bidi:isolate]`} data-testid="chat-weblink">{url}</a>;
}

function Mentions({ text }: { text: string }) {
  return <>{text.split(URL_RX).map((seg, j) => j % 2 === 1
    ? <WebLink key={`u${j}`} url={seg} />
    : seg.split(/(@[\w-]+(?: [A-Z][\w-]*)?)/g).map((p, i) => p.startsWith('@') && p.length > 1
      ? <span key={`${j}-${i}`} dir="ltr" className="text-accent-300 bg-accent-600/15 rounded px-1 py-0.5 font-medium [unicode-bidi:isolate]">{p}</span>
      : <span key={`${j}-${i}`}>{p}</span>))}</>;
}

/** The creative's chat, kept as the default export. */
export default function ChatBox({ creative }: { creative: Creative }) {
  return <ThreadChat kind="creative" id={creative.id} />;
}

/** One chat thread: the creative's, the clinic's room or the company room. People
 *  talk, @mention each other, #link a creative, a strategy, a task or a file, drop a
 *  voice note. Polls for new messages. */
export function ThreadChat({ kind, id, tall = false, defaultOpen = false }: { kind: ChatKind; id: string; tall?: boolean; defaultOpen?: boolean }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  // the on-set feed opens by itself: the crew arrives on the shoot page to post, not to look for a chevron
  const { open: chatOpen, toggle: toggleChat } = useCollapsed(kind === 'shoot' ? 'shoot.feed.open' : kind === 'request' ? 'request.chat.open' : 'creative.chat.open', defaultOpen);
  // A message written on the wrong task should be fixable, and removable. Until now
  // only a video comment could be taken back; a chat message was permanent.
  const reload = () => {
    qc.invalidateQueries({ queryKey: kind === 'creative' ? ['comments', id] : ['thread', kind, id] });
    qc.invalidateQueries({ queryKey: ['chats'] });
  };
  const [editing, setEditing] = useState<{ id: string; body: string; mentions: string[] } | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [refs, setRefs] = useState<CommentRef[]>([]);
  const [visibility, setVisibility] = useState<'internal' | 'client'>('internal');
  const [voice, setVoice] = useState<{ path: string; url: string } | null>(null);
  const [picker, setPicker] = useState(false);
  const [files, setFiles] = useState<ChatAttachment[]>([]);
  const [uploading, setUploading] = useState<{ name: string; pct: number }[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  async function attach(list: FileList | null) {
    if (!list) return;
    for (const f of Array.from(list)) {
      setUploading((cur) => [...cur, { name: f.name, pct: 0 }]);
      try {
        const a = await uploadChatFile(f, (pct) => setUploading((cur) => cur.map((u) => (u.name === f.name ? { ...u, pct } : u))));
        setFiles((cur) => [...cur, a]);
      } catch (e) {
        toast.error(`${f.name}: ${e instanceof Error ? e.message : 'upload failed'}`);
      } finally {
        setUploading((cur) => cur.filter((u) => u.name !== f.name));
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  }
  const listRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: kind === 'creative' ? ['comments', id] : ['thread', kind, id],
    queryFn: () => fetchThreadMessages(kind, id),
    refetchInterval: 8000,
  });
  const count = data?.length ?? 0;

  // Mark read when opened and whenever new messages arrive while open.
  useEffect(() => { markThreadRead(kind, id).then(() => qc.invalidateQueries({ queryKey: ['chats'] })).catch(() => { /* fine */ }); }, [kind, id, count, qc]);
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [count]);

  // every team message must reach someone (Aziz, 2026-09-28); the server checks too
  // a client request's conversation reaches the other side by itself (routers/chat.py)
  const mentionNeeded = !isClient && kind !== 'shoot' && kind !== 'clinic' && kind !== 'request';
  const reachesSomeone = !mentionNeeded || mentions.length > 0 || /@\S/.test(body);
  const post = useMutation({
    mutationFn: () => postThreadMessage(kind, id, body.trim(), visibility, voice?.path ?? null, mentions, refs, files),
    onSuccess: () => {
      setBody(''); setVoice(null); setMentions([]); setRefs([]); setFiles([]);
      qc.invalidateQueries({ queryKey: kind === 'creative' ? ['comments', id] : ['thread', kind, id] });
      if (kind === 'creative') qc.invalidateQueries({ queryKey: ['creatives', id, 'activity'] });
      qc.invalidateQueries({ queryKey: ['chats'] });
    },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not send'),
  });

  function insertRef(h: SearchHit) {
    const token = `#[${h.type}:${h.id}|${h.label}]`;
    setBody((b) => (b.trim() ? `${b.replace(/\s*$/, '')} ${token} ` : `${token} `));
    setRefs((cur) => cur.some((r) => r.type === h.type && r.id === h.id) ? cur : [...cur, { type: h.type, id: h.id, label: h.label, link: h.link }]);
    setPicker(false);
    textareaRef.current?.focus();
  }

  const groups = useMemo(() => {
    const out: { day: string; items: Comment[] }[] = [];
    for (const c of data ?? []) {
      const day = new Date(c.created_at).toLocaleDateString('en-AE', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Dubai' });
      const g = out[out.length - 1];
      if (g && g.day === day) g.items.push(c); else out.push({ day, items: [c] });
    }
    return out;
  }, [data]);

  return (
    <div id="chat"><Card>
      <button type="button" onClick={toggleChat} aria-expanded={chatOpen}
        className={cn('w-full flex items-center gap-2 text-left', chatOpen && 'mb-3')}>
        <CollapseChevron open={chatOpen} />
        <MessageSquare size={16} className="text-accent-400" />
        <h3 className="font-medium text-muted-strong text-sm">Chat</h3>
        <span className="text-xs text-muted ml-auto">{count} message{count === 1 ? '' : 's'}</span>
      </button>
      {chatOpen && (<>

      <div ref={listRef} className={cn('overflow-y-auto pr-1 space-y-4 mb-4', tall ? 'max-h-[65vh] min-h-[40vh]' : 'max-h-[420px]')} data-testid="chat-list">
        {isLoading && <div className="text-xs text-muted">Loading...</div>}
        {!isLoading && count === 0 && (
          <div className="text-xs text-muted py-3">{kind === 'request' ? (isClient ? 'No messages yet. KAF answers here. / لا توجد رسائل بعد، وسيصلك رد KAF هنا.' : 'No messages yet. Ask the clinic what you need, or decide below.') : isClient ? 'Nothing yet. Write to the KAF team here.' : kind === 'company' ? 'Nothing yet. This room is everyone at KAF.' : kind === 'client' ? 'Nothing yet. This room is the team on this clinic. Mark a message client visible to reach the clinic.' : kind === 'shoot' ? 'Nothing from the set yet. Post photos and short clips from the phone as the day goes; the art director is told at once.' : 'Nothing yet. Say what the team should know: a missing shot, an edit note, a question. Use @ for a person and # for a creative, strategy, task or file.'}</div>
        )}
        {groups.map((g) => (
          <div key={g.day}>
            <div className="text-[10px] uppercase tracking-wide text-muted text-center mb-2">{g.day}</div>
            <div className="space-y-2">
              {g.items.map((c) => {
                const mine = c.author_id === user?.id;
                return (
                  <div key={c.id} className={cn('flex gap-2 group/msg', mine ? 'flex-row-reverse' : '')}>
                    <div className="w-7 h-7 rounded-full bg-slate-800 text-[10px] text-muted-strong flex items-center justify-center shrink-0 uppercase" title={c.author_name}>
                      {c.author_name.split(' ').map((w) => w[0]).join('').slice(0, 2)}
                    </div>
                    <div className={cn('max-w-[78%] rounded-2xl px-3 py-2 text-sm', mine ? 'bg-accent-600/20 text-muted-strong rounded-tr-sm' : 'bg-slate-800/60 text-muted-strong rounded-tl-sm')}>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[11px] font-medium text-muted-strong">{c.author_name}</span>
                        {c.visibility === 'client' && kind !== 'request' && <span className="text-[9px] px-1.5 py-0.5 rounded bg-accent-600/20 text-accent-400 uppercase tracking-wide">client visible</span>}
                        <span className="text-[10px] text-muted ml-auto">{new Date(c.created_at).toLocaleTimeString('en-AE', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai' })}</span>
                      </div>
                      {c.body !== '(voice note)' && c.body !== '(file)' && <ChatBody text={c.body} refs={c.refs} />}
                      {c.audio_url && <audio controls src={c.audio_url} className="h-8 mt-1 max-w-full" />}
                      {!!c.attachments?.length && <Attachments items={c.attachments} />}
                      {c.edited_at && <span className="text-[9px] text-muted/70">edited</span>}
                      {/* Your own words, correctable for a short while and removable after */}
                      {mine && (
                        <div className="flex gap-2 mt-1 opacity-0 group-hover/msg:opacity-100 focus-within:opacity-100 transition">
                          <button onClick={() => setEditing({ id: c.id, body: c.body, mentions: c.mentioned_user_ids ?? [] })} data-testid="msg-edit"
                            className="text-[10px] text-muted hover:text-muted-strong">Edit</button>
                          <button onClick={() => setRemoving(c.id)} data-testid="msg-delete"
                            className="text-[10px] text-muted hover:text-red-300">Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <PromptDialog open={!!editing} title="Edit your message"
        message="You can correct your own message for a few minutes after sending it."
        defaultValue={editing?.body ?? ''} multiline mentions defaultMentions={editing?.mentions ?? []}
        maxLength={8000} confirmLabel="Save"
        onCancel={() => setEditing(null)}
        onConfirm={async (v, named) => {
          if (!editing) return;
          try { await editComment(editing.id, v.trim(), named); setEditing(null); reload(); }
          catch (e) { toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not edit'); }
        }} />
      <ConfirmDialog open={!!removing} title="Delete this message?"
        message="It goes for everyone, and it cannot be brought back."
        confirmLabel="Delete" onCancel={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return;
          try { await deleteComment(removing); setRemoving(null); reload(); }
          catch (e) { toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not delete'); }
        }} />

      {/* Composer */}
      <div className="space-y-2 pt-3 border-t border-slate-800">
        {refs.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {refs.map((r) => (
              <span key={`${r.type}:${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 text-sky-300 text-[11px] px-2 py-0.5">
                {ICON[r.type]} {r.label}
                <button onClick={() => { setRefs((cur) => cur.filter((x) => x !== r)); setBody((b) => b.replace(`#[${r.type}:${r.id}|${r.label}]`, '').replace(/\s{2,}/g, ' ')); }} className="hover:text-white"><X size={10} /></button>
              </span>
            ))}
          </div>
        )}
        {(files.length > 0 || uploading.length > 0) && (
          <div className="flex flex-wrap gap-1.5">
            {files.map((a) => (
              <span key={a.path} className="inline-flex items-center gap-1 rounded-full bg-slate-800 text-muted-strong text-[11px] px-2 py-0.5">
                <Paperclip size={10} /> {a.name} <span className="text-muted">{fmtSize(a.size)}</span>
                <button onClick={() => setFiles((cur) => cur.filter((x) => x.path !== a.path))} className="hover:text-white"><X size={10} /></button>
              </span>
            ))}
            {uploading.map((u) => (
              <span key={u.name} className="inline-flex items-center gap-1 rounded-full bg-slate-800/60 text-muted text-[11px] px-2 py-0.5"><Paperclip size={10} /> {u.name} <span className="font-mono">{u.pct}%</span></span>
            ))}
          </div>
        )}
        <MentionTextarea
          textareaRef={textareaRef}
          everyone={isClient || kind === 'clinic' || kind === 'request' ? undefined : kind === 'creative' ? 'Everyone on this creative' : kind === 'company' ? 'The whole team' : 'Everyone on this clinic'}
          value={body} onChange={setBody} mentions={mentions} onMentionsChange={setMentions}
          placeholder={kind === 'request' ? (isClient ? 'Write a reply / اكتب ردك' : 'Write to the clinic. They read it on this request.') : isClient ? 'Write to the team...' : kind === 'clinic' ? 'Write to the clinic. They see this in their portal.' : kind === 'shoot' ? 'From the set: a photo, a clip, a note. @ for a person.' : 'Write to the team. @ for a person, # to link a creative, strategy, task or file.'}
          rows={3}
        />
        {mentionNeeded && !reachesSomeone && (body.trim() || voice || files.length > 0) && (
          <p className="text-[11px] text-amber-300" data-testid="mention-needed">Mention someone with @ so it reaches them, or @everyone for everyone on this chat.</p>
        )}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {!isClient && kind !== 'request' && (
              <div className="relative">
                <button onClick={() => setPicker((v) => !v)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 px-2.5 h-8 text-[11px] text-muted hover:text-muted-strong hover:border-slate-700" title="Link a creative, strategy, task or file" data-testid="chat-link-item">
                  <Hash size={12} /> Link item
                </button>
                {picker && <ItemPicker creativeId={kind === 'creative' ? id : undefined} onPick={insertRef} onClose={() => setPicker(false)} />}
              </div>
            )}
            {!isClient && kind === 'creative' && (
              <div className="flex items-center gap-1 text-[11px] text-muted">
                <button onClick={() => setVisibility('internal')} className={cn('px-2 py-1 rounded-full border transition', visibility === 'internal' ? 'border-accent-400 bg-accent-600/15 text-accent-400' : 'border-slate-800 hover:text-muted-strong')}>Internal</button>
                <button onClick={() => setVisibility('client')} className={cn('px-2 py-1 rounded-full border transition', visibility === 'client' ? 'border-accent-400 bg-accent-600/15 text-accent-400' : 'border-slate-800 hover:text-muted-strong')}>Client visible</button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => attach(e.target.files)} />
            {/* a request's files go on the request itself, where their consent is recorded */}
            {kind !== 'request' && <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 px-2.5 h-8 text-[11px] text-muted hover:text-muted-strong hover:border-slate-700" title="Attach any file; it goes straight to storage" data-testid="chat-attach">
              <Paperclip size={12} /> Attach
            </button>}
            <VoiceRecorder value={voice} onChange={setVoice} disabled={post.isPending} compact />
            <Button size="sm" icon={<Send size={12} />} onClick={() => post.mutate()} loading={post.isPending || uploading.length > 0}
              disabled={(!body.trim() && !voice && files.length === 0) || !reachesSomeone}
              title={reachesSomeone ? undefined : 'Mention someone with @ first'} data-testid="chat-send">
              Send
            </Button>
          </div>
        </div>
      </div>
      </>)}
    </Card></div>
  );
}

function ItemPicker({ creativeId, onPick, onClose }: { creativeId?: string; onPick: (h: SearchHit) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = window.setTimeout(() => setDebounced(q.trim()), 250); return () => window.clearTimeout(t); }, [q]);
  const hits = useQuery({ queryKey: ['search', debounced, creativeId], queryFn: () => searchItems(debounced, creativeId), enabled: debounced.length >= 1 });
  const groups = useMemo(() => {
    const g: Record<string, SearchHit[]> = {};
    for (const h of hits.data ?? []) (g[h.type] ??= []).push(h);
    return g;
  }, [hits.data]);
  const LABEL: Record<string, string> = { creative: 'Creatives', strategy: 'Strategies', task: 'Tasks', asset: 'Files' };
  return (
    <div className="absolute bottom-full mb-2 left-0 z-40 w-80 rounded-xl border border-slate-700 bg-slate-950 shadow-2xl p-2" data-testid="item-picker">
      <div className="flex items-center gap-2 rounded-lg border border-slate-800 bg-bg px-2 h-8 mb-2">
        <Search size={12} className="text-muted" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code, title, file name, stage..." className="flex-1 bg-transparent text-xs text-muted-strong focus:outline-none" onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }} />
        <button onClick={onClose} className="text-muted hover:text-muted-strong"><X size={12} /></button>
      </div>
      <div className="max-h-64 overflow-y-auto">
        {debounced.length === 0 && <div className="text-[11px] text-muted px-2 py-1">Type to find a creative, strategy, task or file.</div>}
        {debounced.length > 0 && hits.isFetching && <div className="text-[11px] text-muted px-2 py-1">Searching...</div>}
        {debounced.length > 0 && !hits.isFetching && (hits.data?.length ?? 0) === 0 && <div className="text-[11px] text-muted px-2 py-1">Nothing matches.</div>}
        {Object.entries(groups).map(([type, list]) => (
          <div key={type} className="mb-1">
            <div className="text-[9px] uppercase tracking-wide text-muted px-2 py-1">{LABEL[type]}</div>
            {list.map((h) => (
              <button key={`${h.type}:${h.id}`} onClick={() => onPick(h)} className="w-full text-left rounded-lg px-2 py-1.5 hover:bg-slate-800/70 flex items-center gap-2">
                <span className="text-sky-300 shrink-0">{ICON[h.type]}</span>
                <span className="text-xs text-muted-strong truncate">{h.label}</span>
                {h.sub && <span className="text-[10px] text-muted truncate ml-auto">{h.sub}</span>}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function fmtSize(n: number): string {
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** Files on a message: images show inline, everything else is a download chip. */
export function Attachments({ items }: { items: ChatAttachment[] }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {items.map((a) => {
        const isImage = (a.content_type || '').startsWith('image/');
        const isVideo = (a.content_type || '').startsWith('video/');
        if (isImage && a.url) return <a key={a.path} href={a.url} target="_blank" rel="noreferrer" title={a.name}><img src={a.url} alt={a.name} className="max-h-40 max-w-[240px] rounded-lg border border-slate-800 object-cover" /></a>;
        if (isVideo && a.url) return <video key={a.path} controls src={a.url} className="max-h-48 max-w-[280px] rounded-lg border border-slate-800" />;
        return (
          <a key={a.path} href={a.url ?? '#'} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 bg-bg px-2.5 py-1.5 text-[11px] text-muted-strong hover:border-accent-400/40">
            <Download size={11} /> <span className="truncate max-w-[220px]">{a.name}</span> <span className="text-muted">{fmtSize(a.size)}</span>
          </a>
        );
      })}
    </div>
  );
}
